import type { VideoSetSource } from "@pax/api-contract/common";

const capabilities = new Map<string, Promise<MediaCapabilitiesDecodingInfo | null>>();

function decodingInfo(source: VideoSetSource) {
  const key = `${source.type}:${source.width}:${source.height}:${source.frameRate}:${source.bitrate}`;
  const existing = capabilities.get(key);
  if (existing) return existing;
  const result = navigator.mediaCapabilities
    ? navigator.mediaCapabilities
        .decodingInfo({
          type: "file",
          video: {
            contentType: source.type,
            width: source.width,
            height: source.height,
            bitrate: source.bitrate,
            framerate: source.frameRate,
          },
        })
        .catch(() => null)
    : Promise.resolve(null);
  capabilities.set(key, result);
  return result;
}

export function browserVideoSource(video: HTMLVideoElement, sources: readonly VideoSetSource[]) {
  const supported = sources.filter(source => video.canPlayType(source.type) !== "");
  const width = video.clientWidth * window.devicePixelRatio;
  return supported.find(source => source.width >= width) ?? supported.at(-1) ?? null;
}

export async function preferredVideoSource(
  video: HTMLVideoElement,
  sources: readonly VideoSetSource[],
) {
  const reports = await Promise.all(sources.map(decodingInfo));
  const candidates = sources.filter(
    (source, index) => reports[index]?.supported !== false && video.canPlayType(source.type) !== "",
  );
  const smooth = candidates.filter(source => reports[sources.indexOf(source)]?.smooth === true);
  const efficient = smooth.filter(
    source => reports[sources.indexOf(source)]?.powerEfficient === true,
  );
  const choices = efficient.length ? efficient : smooth.length ? smooth : candidates;
  const width = video.clientWidth * window.devicePixelRatio;
  return choices.find(source => source.width >= width) ?? choices.at(-1) ?? null;
}
